import type { ReactElement } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';
import { AppRoute } from '../enums/appRoutes';
import { StepKey } from '../enums/stepKey';
import { Layout } from '../Layout';
import { ApplianceDetailsPage } from '../pages/ApplianceDetailsPage/ApplianceDetailsPage';
import { PricePage } from '../pages/PricePage';
import { AppGuard } from './guards/AppGuard';
import { FormGuard } from './guards/FormGuard';
import { FallbackRedirect } from './guards/FallbackRedirect';
import { ArrivalDatePage } from '../pages/ArrivalDatePage';
import { PersonalDetailsPage } from '../pages/PersonalDetails/PersonalDetailsPage';
import { QuotePage } from '../pages/QuotePage/QuotePage';
import { CheckoutPage } from '../pages/CheckoutPage/CheckoutPage';
import { ConfirmationPage } from '../pages/ConfirmationPage/ConfirmationPage';
import { ErrorPage } from '../pages/ErrorPage';
import { Maintenance } from '@domgen/base-design-system-2.0/pages/Maintenance';
import { RegisterPage } from '../pages/RegisterPage/RegisterPage';

export type ClientRoutes = { path: string; title: string; element: ReactElement }[];

const { VITE_APP_PATH, VITE_PR_ID } = import.meta.env;

const appPath =
  `${VITE_PR_ID ? `/${VITE_PR_ID}` : ''}${VITE_APP_PATH ? `/${VITE_APP_PATH}` : VITE_PR_ID ? '' : '/'}`.trim();

const landingHeroPath = `${appPath?.replace(/\/$/, '')}/images/landing-hero.avif`;
const landingBrandPath = `${appPath?.replace(/\/$/, '')}/images/landing-brand.avif`;

export const formStepRoutes = [
  {
    path: AppRoute.LANDING,
    element: <RegisterPage heroBackground={landingHeroPath} brandImage={landingBrandPath} />,
    stepKey: StepKey.MODEL_SERIAL,
    title: 'Appliance Details',
  },
  {
    path: AppRoute.APPLIANCE_DETAILS,
    element: <ApplianceDetailsPage totalSteps={5} stepNumber={1} />,
    stepKey: StepKey.APPLIANCE_DETAILS,
    title: 'Appliance Details',
  },
  {
    path: AppRoute.PURCHASE_PRICE,
    element: <PricePage totalSteps={5} stepNumber={3} />,
    stepKey: StepKey.PURCHASE_PRICE,
    title: 'Purchase Price',
  },
  {
    path: AppRoute.ARRIVAL_DATE,
    element: <ArrivalDatePage totalSteps={5} stepNumber={4} />,
    stepKey: StepKey.ARRIVAL_DATE,
    title: 'Arrival Date',
  },
  {
    path: AppRoute.PERSONAL_DETAILS,
    element: <PersonalDetailsPage totalSteps={5} stepNumber={5} />,
    stepKey: StepKey.PERSONAL_DETAILS,
    title: 'Personal Details',
  },
];

export const additionalRoutes = [
  {
    path: AppRoute.QUOTE,
    element: <QuotePage />,
    title: 'Your Quote',
  },
  {
    path: AppRoute.CONFIRMATION,
    element: <ConfirmationPage />,
    title: 'Confirmation',
  },
  {
    path: AppRoute.CHECKOUT,
    element: <CheckoutPage />,
    title: 'Checkout',
  },
  {
    path: AppRoute.ERROR,
    element: <ErrorPage />,
    title: 'Error',
  },
];

export const buildRouter = (clientRoutes: ClientRoutes | undefined = []) => {
  const env = import.meta.env;
  const allRoutes = [...formStepRoutes, ...additionalRoutes, ...clientRoutes];

  const getRouteFromPath = (pathname: string) => allRoutes.find((route) => route.path === pathname);

  const isMaintenanceModeEnabled = env.VITE_MAINTENANCE_MODE_ENABLED === 'true';

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<Layout />}>
        {isMaintenanceModeEnabled ? (
          <>
            <Route path="*" element={<Maintenance phoneNumber="+1-855-321-3007" />} />
          </>
        ) : (
          <Route element={<AppGuard />}>
            <Route element={<FormGuard />}>
              {formStepRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
            </Route>
            {[...additionalRoutes, ...clientRoutes].map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
            <Route path="*" element={<FallbackRedirect />} />
          </Route>
        )}
      </Route>,
    ),
    { basename: appPath },
  );

  return { router, getRouteFromPath };
};

export const getNextStepPath = (stepKey: StepKey) => {
  const currentRouteIndex = formStepRoutes.findIndex((route) => route.stepKey === stepKey);
  if (currentRouteIndex === -1 || currentRouteIndex === formStepRoutes.length - 1) {
    return null;
  }
  return formStepRoutes[currentRouteIndex + 1].path;
};

export const getPreviousStepPath = (stepKey: StepKey) => {
  const currentRouteIndex = formStepRoutes.findIndex((route) => route.stepKey === stepKey);
  if (currentRouteIndex <= 0) {
    return null;
  }
  return formStepRoutes[currentRouteIndex - 1].path;
};
